import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Chip,
  Alert,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  IconButton,
  CircularProgress,
  InputAdornment,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import {
  ExpandMore,
  Security,
  Notifications,
  Language,
  Palette,
  Email,
  Phone,
  Lock,
  Visibility,
  VisibilityOff,
  Save,
  Refresh,
  LightMode,
  DarkMode,
  VpnKey
} from '@mui/icons-material';
import { useTheme } from '../../context/ThemeContext';
import { sendVerificationCode, verifyCode, updateApprovalPin } from '../../services/auth';
import { hasEndpointAccess } from '../../utils/rbac';

const AccountSettings = () => {
  const user = useSelector(state => state.auth.user);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { mode, toggleTheme } = useTheme();
  
  // Approval PIN Management State
  const [pinStep, setPinStep] = useState(0); // 0: Send OTP, 1: Verify OTP, 2: Set PIN
  const [otpCode, setOtpCode] = useState('');
  const [approvalPin, setApprovalPin] = useState('');
  const [confirmApprovalPin, setConfirmApprovalPin] = useState('');
  const [showApprovalPin, setShowApprovalPin] = useState(false);
  const [showConfirmApprovalPin, setShowConfirmApprovalPin] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [loadingPin, setLoadingPin] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(true);
  
  // Check if user has permission to update approval PIN
  const canUpdateApprovalPin = user && hasEndpointAccess(user, 'admin-update-approval-pin', 'POST');

  const [settings, setSettings] = useState({
    // Security Settings
    twoFactorAuth: true,
    loginNotifications: true,
    sessionTimeout: 30,
    passwordExpiry: 90,
    
    // Notification Settings
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    orderUpdates: true,
    inventoryAlerts: true,
    marketingEmails: false,
    
    // Language & Region
    language: 'English',
    timezone: 'Africa/Lagos',
    currency: 'NGN',
    dateFormat: 'DD/MM/YYYY',
    
    // Display Settings
    compactMode: false,
    showAnalytics: true,
    
    // Password Change
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    // Load sessionTimeout from localStorage on mount
    const savedTimeout = localStorage.getItem('sessionTimeout');
    if (savedTimeout) {
      setSettings(prev => ({ ...prev, sessionTimeout: Number(savedTimeout) }));
    }
  }, []);

  useEffect(() => {
    // Persist sessionTimeout to localStorage whenever it changes
    localStorage.setItem('sessionTimeout', settings.sessionTimeout);
  }, [settings.sessionTimeout]);
  
  // Resend timer effect for OTP
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);
  
  // Format phone number for display (mask middle digits)
  const formatPhoneForDisplay = (phone) => {
    if (!phone) return 'N/A';
    // Remove leading + if present
    const cleaned = phone.replace(/^\+/, '');
    if (cleaned.length >= 10) {
      // Show first 3 and last 3 digits, mask the middle
      return `${cleaned.substring(0, 3)}****${cleaned.substring(cleaned.length - 3)}`;
    }
    return phone;
  };
  
  // Handle send OTP for approval PIN
  const handleSendOTPForPin = async () => {
    if (!user?.phone) {
      setSnackbar({ open: true, message: 'No phone number on record. Please contact administrator.', severity: 'error' });
      return;
    }
    
    setLoadingPin(true);
    try {
      // Format phone number (remove leading + if present)
      const phone = user.phone.replace(/^\+/, '');
      await sendVerificationCode({ type: 'sms', phone });
      setOtpSent(true);
      setPinStep(1);
      setResendTimer(60);
      setCanResend(false);
      setSnackbar({ open: true, message: `OTP sent to ${formatPhoneForDisplay(user.phone)}`, severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Failed to send OTP', severity: 'error' });
    } finally {
      setLoadingPin(false);
    }
  };
  
  // Handle verify OTP for approval PIN
  const handleVerifyOTPForPin = async () => {
    if (!otpCode.trim() || otpCode.length !== 6) {
      setSnackbar({ open: true, message: 'Please enter a valid 6-digit OTP code', severity: 'error' });
      return;
    }
    
    setLoadingPin(true);
    try {
      const phone = user.phone.replace(/^\+/, '');
      await verifyCode({ type: 'sms', phone, code: otpCode });
      setOtpVerified(true);
      setPinStep(2);
      setSnackbar({ open: true, message: 'OTP verified successfully', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Invalid OTP code', severity: 'error' });
    } finally {
      setLoadingPin(false);
    }
  };
  
  // Handle resend OTP
  const handleResendOTP = async () => {
    if (!canResend) return;
    await handleSendOTPForPin();
  };
  
  // Handle update approval PIN
  const handleUpdateApprovalPin = async () => {
    if (!approvalPin.trim() || approvalPin.length !== 6 || !approvalPin.match(/^\d{6}$/)) {
      setSnackbar({ open: true, message: 'Please enter a valid 6-digit PIN', severity: 'error' });
      return;
    }
    
    if (approvalPin !== confirmApprovalPin) {
      setSnackbar({ open: true, message: 'PINs do not match', severity: 'error' });
      return;
    }
    
    if (!otpVerified) {
      setSnackbar({ open: true, message: 'Please verify OTP first', severity: 'error' });
      return;
    }
    
    setLoadingPin(true);
    try {
      await updateApprovalPin(approvalPin, otpCode);
      setSnackbar({ open: true, message: 'Approval PIN updated successfully', severity: 'success' });
      // Reset form
      setPinStep(0);
      setOtpCode('');
      setApprovalPin('');
      setConfirmApprovalPin('');
      setOtpSent(false);
      setOtpVerified(false);
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Failed to update approval PIN', severity: 'error' });
    } finally {
      setLoadingPin(false);
    }
  };
  
  // Reset approval PIN form
  const handleResetPinForm = () => {
    setPinStep(0);
    setOtpCode('');
    setApprovalPin('');
    setConfirmApprovalPin('');
    setOtpSent(false);
    setOtpVerified(false);
    setResendTimer(0);
    setCanResend(true);
  };

  const handleSettingChange = (setting, value) => {
    setSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const handleSaveSettings = () => {
    setSnackbar({ open: true, message: 'Settings saved successfully!', severity: 'success' });
  };

  const handlePasswordChange = () => {
    if (settings.newPassword !== settings.confirmPassword) {
      setSnackbar({ open: true, message: 'New passwords do not match!', severity: 'error' });
      return;
    }
    if (settings.newPassword.length < 8) {
      setSnackbar({ open: true, message: 'Password must be at least 8 characters long!', severity: 'error' });
      return;
    }
    setSnackbar({ open: true, message: 'Password changed successfully!', severity: 'success' });
    handleSettingChange('currentPassword', '');
    handleSettingChange('newPassword', '');
    handleSettingChange('confirmPassword', '');
  };

  const languages = ['English', 'French', 'Spanish', 'Arabic', 'Chinese'];
  const timezones = ['Africa/Lagos', 'America/New_York', 'Europe/London', 'Asia/Tokyo'];
  const currencies = ['NGN', 'USD', 'EUR', 'GBP', 'JPY'];
  const dateFormats = ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'];

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', color: '#DC143C' }}>
        Account Settings
      </Typography>

      <Grid container spacing={3}>
        {/* Security Settings */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Security sx={{ mr: 2, color: '#DC143C' }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
                  Security Settings
                </Typography>
              </Box>
              
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Lock />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Two-Factor Authentication" 
                    secondary="Add an extra layer of security to your account"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings.twoFactorAuth}
                      onChange={(e) => handleSettingChange('twoFactorAuth', e.target.checked)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#DC143C' } }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <Email />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Login Notifications" 
                    secondary="Get notified when someone logs into your account"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings.loginNotifications}
                      onChange={(e) => handleSettingChange('loginNotifications', e.target.checked)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#DC143C' } }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Session & Password Settings
              </Typography>
              
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Session Timeout (minutes)</InputLabel>
                    <Select
                      value={settings.sessionTimeout}
                      onChange={(e) => handleSettingChange('sessionTimeout', e.target.value)}
                      label="Session Timeout (minutes)"
                    >
                      <MenuItem value={15}>15 minutes</MenuItem>
                      <MenuItem value={30}>30 minutes</MenuItem>
                      <MenuItem value={60}>1 hour</MenuItem>
                      <MenuItem value={120}>2 hours</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Password Expiry (days)</InputLabel>
                    <Select
                      value={settings.passwordExpiry}
                      onChange={(e) => handleSettingChange('passwordExpiry', e.target.value)}
                      label="Password Expiry (days)"
                    >
                      <MenuItem value={30}>30 days</MenuItem>
                      <MenuItem value={60}>60 days</MenuItem>
                      <MenuItem value={90}>90 days</MenuItem>
                      <MenuItem value={180}>180 days</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Notification Settings */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Notifications sx={{ mr: 2, color: '#DC143C' }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
                  Notification Settings
                </Typography>
              </Box>
              
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Email />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Email Notifications" 
                    secondary="Receive notifications via email"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings.emailNotifications}
                      onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#DC143C' } }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <Phone />
                  </ListItemIcon>
                  <ListItemText 
                    primary="SMS Notifications" 
                    secondary="Receive notifications via SMS"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings.smsNotifications}
                      onChange={(e) => handleSettingChange('smsNotifications', e.target.checked)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#DC143C' } }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <Notifications />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Push Notifications" 
                    secondary="Receive browser push notifications"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings.pushNotifications}
                      onChange={(e) => handleSettingChange('pushNotifications', e.target.checked)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#DC143C' } }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Notification Types
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.orderUpdates}
                      onChange={(e) => handleSettingChange('orderUpdates', e.target.checked)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#DC143C' } }}
                    />
                  }
                  label="Order Updates"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.inventoryAlerts}
                      onChange={(e) => handleSettingChange('inventoryAlerts', e.target.checked)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#DC143C' } }}
                    />
                  }
                  label="Inventory Alerts"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.marketingEmails}
                      onChange={(e) => handleSettingChange('marketingEmails', e.target.checked)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#DC143C' } }}
                    />
                  }
                  label="Marketing Emails"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Language & Region */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Language sx={{ mr: 2, color: '#DC143C' }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
                  Language & Region
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <FormControl fullWidth>
                    <InputLabel>Language</InputLabel>
                    <Select
                      value={settings.language}
                      onChange={(e) => handleSettingChange('language', e.target.value)}
                      label="Language"
                    >
                      {languages.map((lang) => (
                        <MenuItem key={lang} value={lang}>{lang}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <FormControl fullWidth>
                    <InputLabel>Timezone</InputLabel>
                    <Select
                      value={settings.timezone}
                      onChange={(e) => handleSettingChange('timezone', e.target.value)}
                      label="Timezone"
                    >
                      {timezones.map((tz) => (
                        <MenuItem key={tz} value={tz}>{tz}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Currency</InputLabel>
                    <Select
                      value={settings.currency}
                      onChange={(e) => handleSettingChange('currency', e.target.value)}
                      label="Currency"
                    >
                      {currencies.map((curr) => (
                        <MenuItem key={curr} value={curr}>{curr}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Date Format</InputLabel>
                    <Select
                      value={settings.dateFormat}
                      onChange={(e) => handleSettingChange('dateFormat', e.target.value)}
                      label="Date Format"
                    >
                      {dateFormats.map((format) => (
                        <MenuItem key={format} value={format}>{format}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Display Settings */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Palette sx={{ mr: 2, color: '#DC143C' }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
                  Display Settings
                </Typography>
              </Box>
              
              <List>
                <ListItem>
                  <ListItemIcon>
                    {mode === 'light' ? <LightMode /> : <DarkMode />}
                  </ListItemIcon>
                  <ListItemText 
                    primary="Theme Mode" 
                    secondary={`Currently using ${mode} mode`}
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={mode === 'dark'}
                      onChange={toggleTheme}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#DC143C' } }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <Palette />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Compact Mode" 
                    secondary="Reduce spacing for more content"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings.compactMode}
                      onChange={(e) => handleSettingChange('compactMode', e.target.checked)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#DC143C' } }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <Notifications />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Show Analytics Dashboard" 
                    secondary="Display analytics on main dashboard"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings.showAnalytics}
                      onChange={(e) => handleSettingChange('showAnalytics', e.target.checked)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#DC143C' } }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Approval PIN Management */}
        {canUpdateApprovalPin && (
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <VpnKey sx={{ mr: 2, color: '#DC143C' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
                    Approval PIN Management
                  </Typography>
                </Box>
                
                <Alert severity="info" sx={{ mb: 3 }}>
                  Set or update your 6-digit approval PIN. This PIN is required to approve orders placed on behalf of customers.
                  You will receive an OTP on your registered phone number: <strong>{formatPhoneForDisplay(user?.phone)}</strong>
                </Alert>
                
                <Stepper activeStep={pinStep} sx={{ mb: 3 }}>
                  <Step>
                    <StepLabel>Send OTP</StepLabel>
                  </Step>
                  <Step>
                    <StepLabel>Verify OTP</StepLabel>
                  </Step>
                  <Step>
                    <StepLabel>Set PIN</StepLabel>
                  </Step>
                </Stepper>
                
                {pinStep === 0 && (
                  <Box>
                    <Button
                      variant="contained"
                      onClick={handleSendOTPForPin}
                      disabled={loadingPin || !user?.phone}
                      sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
                    >
                      {loadingPin ? <CircularProgress size={24} color="inherit" /> : 'Send OTP to Phone'}
                    </Button>
                  </Box>
                )}
                
                {pinStep === 1 && (
                  <Box>
                    <TextField
                      fullWidth
                      label="Enter OTP Code"
                      value={otpCode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setOtpCode(value);
                      }}
                      placeholder="123456"
                      inputProps={{ maxLength: 6 }}
                      disabled={loadingPin}
                      sx={{ mb: 2 }}
                    />
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={handleResetPinForm}
                        disabled={loadingPin}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleVerifyOTPForPin}
                        disabled={loadingPin || otpCode.length !== 6}
                        sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
                      >
                        {loadingPin ? <CircularProgress size={24} color="inherit" /> : 'Verify OTP'}
                      </Button>
                    </Box>
                    {canResend ? (
                      <Button
                        variant="text"
                        onClick={handleResendOTP}
                        disabled={loadingPin}
                        sx={{ color: '#DC143C' }}
                      >
                        Resend Code
                      </Button>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Resend code in {resendTimer} seconds
                      </Typography>
                    )}
                  </Box>
                )}
                
                {pinStep === 2 && (
                  <Box>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          type={showApprovalPin ? 'text' : 'password'}
                          label="New Approval PIN"
                          value={approvalPin}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setApprovalPin(value);
                          }}
                          placeholder="Enter 6-digit PIN"
                          inputProps={{ maxLength: 6 }}
                          disabled={loadingPin}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowApprovalPin(!showApprovalPin)}
                                  edge="end"
                                >
                                  {showApprovalPin ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                          helperText={approvalPin.length > 0 && approvalPin.length < 6 ? 'PIN must be 6 digits' : ''}
                          error={approvalPin.length > 0 && approvalPin.length < 6}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          type={showConfirmApprovalPin ? 'text' : 'password'}
                          label="Confirm Approval PIN"
                          value={confirmApprovalPin}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setConfirmApprovalPin(value);
                          }}
                          placeholder="Confirm 6-digit PIN"
                          inputProps={{ maxLength: 6 }}
                          disabled={loadingPin}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowConfirmApprovalPin(!showConfirmApprovalPin)}
                                  edge="end"
                                >
                                  {showConfirmApprovalPin ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                          helperText={approvalPin !== confirmApprovalPin && confirmApprovalPin.length > 0 ? 'PINs do not match' : ''}
                          error={approvalPin !== confirmApprovalPin && confirmApprovalPin.length > 0}
                        />
                      </Grid>
                    </Grid>
                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={handleResetPinForm}
                        disabled={loadingPin}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleUpdateApprovalPin}
                        disabled={loadingPin || approvalPin.length !== 6 || approvalPin !== confirmApprovalPin}
                        sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
                      >
                        {loadingPin ? <CircularProgress size={24} color="inherit" /> : 'Update Approval PIN'}
                      </Button>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Change Password */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: '#DC143C' }}>
                Change Password
              </Typography>
              
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    label="Current Password"
                    value={settings.currentPassword}
                    onChange={(e) => handleSettingChange('currentPassword', e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      )
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type={showNewPassword ? 'text' : 'password'}
                    label="New Password"
                    value={settings.newPassword}
                    onChange={(e) => handleSettingChange('newPassword', e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      )
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type={showConfirmPassword ? 'text' : 'password'}
                    label="Confirm New Password"
                    value={settings.confirmPassword}
                    onChange={(e) => handleSettingChange('confirmPassword', e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      )
                    }}
                  />
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handlePasswordChange}
                  sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
                >
                  Change Password
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    handleSettingChange('currentPassword', '');
                    handleSettingChange('newPassword', '');
                    handleSettingChange('confirmPassword', '');
                  }}
                >
                  Clear
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Save Settings */}
        <Grid size={{ xs: 12 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => window.location.reload()}
            >
              Reset to Default
            </Button>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSaveSettings}
              sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
            >
              Save All Settings
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={3000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AccountSettings; 